/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { useTheme } from "../../other/theme";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

interface IProps {
    size?: number;
    color?: string;
}

export const Spinner = (props: IProps) => {
    const theme = useTheme();
    const color = props.color ?? theme.foreground;

    return (
        <Container color={color}>
            <BlueprintSpinner size={props.size ?? 40} />
        </Container>
    );
};

const Container = styled("div")<{ color: string }>(props => ({
    display: "inline-block",
    ".spinner-head": {
        stroke: props.color
    }
}));

/*
 * Copyright 2015 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// see http://stackoverflow.com/a/18473154/3124288 for calculating arc path
const R = 45;
const SPINNER_TRACK = `M 50,50 m 0,-${R} a ${R},${R} 0 1 1 0,${
    R * 2
} a ${R},${R} 0 1 1 0,-${R * 2}`;

// unitless total length of SVG path, to which stroke-dash* properties are relative.
// https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/pathLength
// this value is the result of `<path d={SPINNER_TRACK} />.getTotalLength()` and works in all browsers:
const PATH_LENGTH = 280;

const MIN_SIZE = 10;
const STROKE_WIDTH = 4;
const MIN_STROKE_WIDTH = 16;

const SIZE_LARGE = 100;

const BlueprintSpinner = (props: { size: number; value?: number }) => {
    const size = getSize(props.size);

    // keep spinner track width consistent at all sizes (down to about 10px).
    const strokeWidth = Math.min(
        MIN_STROKE_WIDTH,
        (STROKE_WIDTH * SIZE_LARGE) / size
    );
    const strokeOffset =
        PATH_LENGTH -
        PATH_LENGTH *
            (props.value === undefined
                ? 0.25
                : (clamp(props.value, 0, 1) ?? 0));

    // multiple DOM elements around SVG are necessary to properly isolate animation:
    // - SVG elements in IE do not support anim/trans so they must be set on a parent HTML element.
    // - SPINNER_ANIMATION isolates svg from parent display and is always centered inside root element.
    return (
        <Styles>
            <div className="spinner">
                <div className="spinner-animation">
                    <svg
                        width={size}
                        height={size}
                        strokeWidth={strokeWidth.toFixed(2)}
                        viewBox={getViewBox(strokeWidth)}
                    >
                        <path className="spinner-track" d={SPINNER_TRACK} />
                        <path
                            className="spinner-head"
                            d={SPINNER_TRACK}
                            pathLength={PATH_LENGTH}
                            strokeDasharray={`${PATH_LENGTH} ${PATH_LENGTH}`}
                            strokeDashoffset={strokeOffset}
                        />
                    </svg>
                </div>
            </div>
        </Styles>
    );
};

function clamp(val: number | null, min: number, max: number) {
    if (val == null) {
        return val;
    }
    if (max < min) {
        throw new Error("clamp error");
    }
    return Math.min(Math.max(val, min), max);
}

function getSize(size: number) {
    return Math.max(MIN_SIZE, size);
}

function getViewBox(strokeWidth: number) {
    const radius = R + strokeWidth / 2;
    const viewBoxX = (50 - radius).toFixed(2);
    const viewBoxWidth = (radius * 2).toFixed(2);
    return `${viewBoxX} ${viewBoxX} ${viewBoxWidth} ${viewBoxWidth}`;
}

const animation = keyframes`
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
`;

const Styles = styled("div")({
    ".spinner": {
        alignItems: "center",
        // center animation container inside parent element to isolate layout
        display: "flex",
        justifyContent: "center",

        // allow paths to overflow container -- critical for edges of circles!
        overflow: "visible",
        verticalAlign: "middle",

        svg: {
            display: "block"
        },

        path: {
            fillOpacity: 0
        },

        ".spinner-head": {
            strokeLinecap: "round",
            transformOrigin: "center",
            transition:
                "stroke-dashoffset 200ms cubic-bezier(0.4, 1, 0.75, 0.9)"
        },

        ".spinner-track": {
            stroke: "rgba(92, 112, 128, 0.2)"
        }
    },

    // put the animation on a child HTML element to isolate it from display of parent
    ".spinner-animation": {
        animation: `${animation} 500ms linear infinite`,

        ".no-spin > &": {
            animation: "none"
        }
    }
});
